import { Injectable } from "@angular/core";
import { NotificationApiService } from "@services/notification-api/notification-api.service";
import { BehaviorSubject, map, Observable, of } from "rxjs";

interface NotificationListResponse {
  messages?: any[];
  totalPages?: number;
  totalUnseen?: number;
  asOfDate?: string;
}

@Injectable({ providedIn: "root" })
export class NotificationFacade {
  private readonly responseSubject$ =
    new BehaviorSubject<NotificationListResponse>({
      messages: [],
      totalPages: 0,
      totalUnseen: 0,
      asOfDate: "",
    });
  private activeUserId: string | null = null;
  private loadedFirstPageUserId: string | null = null;

  private readonly response$ = this.responseSubject$.asObservable();
  public readonly messages$ = this.response$.pipe(
    map((response) => response?.messages ?? []),
  );
  public readonly unreadCount$ = this.response$.pipe(
    map((response) => {
      if (typeof response?.totalUnseen === "number") {
        return response.totalUnseen;
      }
      return (response?.messages ?? []).filter((message) => !message?.seenInd)
        .length;
    }),
  );

  constructor(
    private readonly notificationApiService: NotificationApiService,
  ) {}

  public loadFirstPage(userId: string): Observable<NotificationListResponse> {
    if (!userId) {
      this.reset();
      return of(this.responseSubject$.value);
    }

    if (this.loadedFirstPageUserId === userId) {
      return of(this.responseSubject$.value);
    }

    this.activateUser(userId);

    // Notification endpoint is temporarily disabled.
    // return this.notificationApiService
    //   .getNotificationList({
    //     userId,
    //     page: 0,
    //     size: 10,
    //   })
    //   .pipe(
    //     tap((response) => {
    //       if (this.activeUserId !== userId) {
    //         return;
    //       }
    //
    //       this.loadedFirstPageUserId = userId;
    //       this.setResponse(response);
    //     }),
    //   );

    this.loadedFirstPageUserId = userId;
    return of(this.responseSubject$.value);
  }

  public setResponse(response: NotificationListResponse): void {
    this.responseSubject$.next({
      messages: response?.messages ?? [],
      totalPages: response?.totalPages ?? 0,
      totalUnseen: response?.totalUnseen ?? 0,
      asOfDate: response?.asOfDate ?? "",
    });
  }

  public reset(): void {
    this.activeUserId = null;
    this.loadedFirstPageUserId = null;
    this.clearResponse();
  }

  private activateUser(userId: string): void {
    if (this.activeUserId === userId) {
      return;
    }

    this.activeUserId = userId;
    this.loadedFirstPageUserId = null;
    this.clearResponse();
  }

  private clearResponse(): void {
    this.responseSubject$.next({
      messages: [],
      totalPages: 0,
      totalUnseen: 0,
      asOfDate: "",
    });
  }
}
